import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import type { CategoryWithChildren } from "../../helpers/productsCategoryHelpers";
import { hasCategoryProducts } from "../../helpers/productsCategoryHelpers";

interface CategoriesBarNarrowProps {
  categories: CategoryWithChildren[];
  activeCategoryIds: number[];
  onCategoryClick: (categoryId: number | null) => void;
  isLoading?: boolean;
}

const ExpandIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
    aria-hidden="true"
    className="expand-icon"
  >
    <path
      fillRule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
  </svg>
);

const CategoriesBarNarrow: React.FC<CategoriesBarNarrowProps> = ({
  categories,
  activeCategoryIds,
  onCategoryClick,
  isLoading = false,
}) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<number>>(
    new Set()
  );
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const toggleParentExpansion = (
    parentId: number,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    setExpandedParents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  };

  const handleCategorySelect = (categoryId: number | null) => {
    onCategoryClick(categoryId);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getButtonText = () => {
    if (activeCategoryIds.length === 1) {
      const findCat = (
        id: number,
        nodes: CategoryWithChildren[]
      ): CategoryWithChildren | null => {
        for (const node of nodes) {
          if (node.id === id) return node;
          const found = findCat(id, node.children);
          if (found) return found;
        }
        return null;
      };
      const activeCat = findCat(activeCategoryIds[0], categories);
      return activeCat?.name || t("productArchive.categories");
    }
    return t("productArchive.categories");
  };

  const renderCategoriesRecursive = (
    cats: CategoryWithChildren[],
    level = 0
  ) => {
    return (
      <ul className={`categories-narrow__sublist level-${level}`}>
        {cats.map((cat) => {
          const hasChildren = cat.children?.some(hasCategoryProducts);
          const isExpanded = expandedParents.has(cat.id);
          const isActive = activeCategoryIds.includes(cat.id);
          return (
            <li
              key={cat.id}
              className={`categories-narrow__item ${
                hasChildren ? "has-children" : ""
              } ${isActive ? "is-active" : ""}`}
            >
              <div className="categories-narrow__item-content">
                <a
                  href="#"
                  className="categories-narrow__link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleCategorySelect(cat.id);
                  }}
                >
                  {cat.name}
                </a>
                {hasChildren && (
                  <button
                    className={`categories-narrow__expand ${
                      isExpanded ? "is-expanded" : ""
                    }`}
                    onClick={(e) => toggleParentExpansion(cat.id, e)}
                  >
                    <ExpandIcon />
                  </button>
                )}
              </div>
              {hasChildren && isExpanded && (
                <div className="categories-narrow__children">
                  {renderCategoriesRecursive(
                    cat.children.filter(hasCategoryProducts),
                    level + 1
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="categories-bar categories-bar--narrow" ref={dropdownRef}>
      {isLoading ? (
        <Skeleton height={40} width={150} />
      ) : (
        <button
          className="categories-narrow__trigger"
          onClick={toggleDropdown}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          {getButtonText()} <span className="dropdown-arrow">▼</span>
        </button>
      )}

      {isOpen && !isLoading && (
        <div className="categories-narrow__dropdown">
          <ul className="categories-narrow__list">
            <li
              className={`categories-narrow__item ${
                activeCategoryIds.length === 0 ? "is-active" : ""
              }`}
            >
              <a
                href="#"
                className="categories-narrow__link"
                onClick={(e) => {
                  e.preventDefault();
                  handleCategorySelect(null);
                }}
              >
                {t("productArchive.allProducts")}
              </a>
            </li>
            {renderCategoriesRecursive(categories)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CategoriesBarNarrow;
